import React, { FC, PropsWithChildren, useCallback, useState } from 'react';
import { AuthProvider, type User } from 'oidc-react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import {
	consumeLegacyReturnTo,
	getCurrentReturnTo,
	getReturnToFromState,
	redirectToSignIn,
	userManager,
} from '../../utils/oidc';
import { ProtectedArea } from '../ProtectedArea';

export const OidcAuth: FC<PropsWithChildren> = ({ children }) => {
	const navigate = useNavigate();
	const [signInError, setSignInError] = useState<Error | null>(null);

	const handleBeforeSignIn = useCallback(
		() => ({ returnTo: getCurrentReturnTo() }),
		[],
	);

	const handleSignIn = useCallback(
		(user: User | null) => {
			setSignInError(null);
			const legacyReturnTo = consumeLegacyReturnTo();
			const returnTo =
				getReturnToFromState(user?.state) || legacyReturnTo || '/';

			navigate(returnTo, { replace: true });
		},
		[navigate],
	);

	const handleSignInError = useCallback((error: Error) => {
		setSignInError(error);
		Sentry.captureException(error, {
			tags: { section: 'oidc-auth-sign-in' },
		});
	}, []);

	const handleRetry = useCallback(() => {
		setSignInError(null);
		redirectToSignIn(true).catch(error => {
			handleSignInError(
				error instanceof Error
					? error
					: new Error('Unable to start sign in'),
			);
		});
	}, [handleSignInError]);

	return (
		<AuthProvider
			userManager={userManager}
			autoSignOut={false}
			onBeforeSignIn={handleBeforeSignIn}
			onSignIn={handleSignIn}
			onSignInError={handleSignInError}>
			<ProtectedArea authError={signInError} onRetry={handleRetry}>
				{children}
			</ProtectedArea>
		</AuthProvider>
	);
};
